import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LmsAssetUpload from "./lmsAssetUpload";
import LmsAssetRenderer from "./lmsAssetRenderer";
import { assetIcon, formatDuration, assetIdOf } from "../../admin/_lmsHelpers";
import VideoCaptionTrack from "./videoCaptionTrack";

const formatAssetSize = (asset = {}) => {
  const raw =
    asset.sizeBytes ??
    asset.fileSizeBytes ??
    asset.fileSize ??
    asset.size ??
    asset.sizeMb;
  const value = Number(raw);

  if (!value) return null;
  if (asset.sizeMb || value < 1000) return `${value.toFixed(value >= 10 ? 0 : 1)} MB`;

  const mb = value / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;

  const kb = value / 1024;
  return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
};

const assetCopyForAccept = (accept = "", t) => {
  const normalized = accept.toLowerCase();
  const acceptsVideo = normalized.includes("video");
  const acceptsPdf = normalized.includes("pdf");
  const acceptsImage = normalized.includes("image");

  if (acceptsVideo && !acceptsPdf && !acceptsImage) {
    return {
      empty: t("admin.lms.asset.emptyVideo", "No video yet."),
      upload: t("admin.lms.asset.uploadVideo", "Upload Video"),
    };
  }

  if (acceptsPdf && !acceptsVideo && !acceptsImage) {
    return {
      empty: t("admin.lms.asset.emptyPdf", "No PDF yet."),
      upload: t("admin.lms.asset.uploadPdf", "Upload PDF"),
    };
  }

  if (acceptsImage && !acceptsVideo && !acceptsPdf) {
    return {
      empty: t("admin.lms.asset.emptyImage", "No image yet."),
      upload: t("admin.lms.asset.uploadImage", "Upload Image"),
    };
  }

  return {
    empty: t("admin.lms.asset.empty"),
    upload: t("admin.lms.asset.add"),
  };
};

const LmsAssetList = (props) => {
  const { t } = useTranslation();

  const {
    assets = [],
    onAdd,
    showButton = true,
    maxItems = Infinity,
    onDelete,
    onReorder,
    accept,
    addLabel,
    uploadTitle,
    assetKind,
    emptyTitle,
    emptyBody,
    helperText,
    reorderable = true,
    variant,
    uploadContext,
  } = props;

  const [successDismissedCount, setSuccessDismissedCount] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const uploadCopy = assetCopyForAccept(accept, t);
  const canUpload = showButton && assets.length < maxItems;
  const isModuleIntro = variant === "moduleIntro";
  const isPartAsset = variant === "partAsset";
  const isKeyTips = variant === "keyTips";

  const move = (i, dir) => {
    const tgt = i + dir;
    if (tgt < 0 || tgt >= assets.length) return;
    const next = [...assets];
    [next[i], next[tgt]] = [next[tgt], next[i]];
    onReorder?.(next);
  };
  const dropMove = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...assets];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDragIndex(null);
    onReorder?.(next);
  };
  const renderAssetCards = () =>
    assets.map((a, i) => {
      const dur = formatDuration(a.durationSeconds);
      const src = a.previewUrl || a.signedUrl || a.url;

      return (
        <div className="lms-asset-card" key={assetIdOf(a) || i}>
          {a.type === "image" && src ? (
            <span className="lms-asset-thumb">
              <img src={src} alt="" />
            </span>
          ) : a.type === "video" && src ? (
            <span className="lms-asset-thumb">
              <video src={src} muted preload="metadata">
                <VideoCaptionTrack asset={a} />
              </video>
              <i className="fas fa-play lms-asset-thumb-play"></i>
            </span>
          ) : (
            <span className="lms-asset-icon">
              <i className={assetIcon(a.type)}></i>
            </span>
          )}
          <div className="lms-asset-main">
            <span className="lms-asset-title">
              {a.title || a.filename || "-"}
            </span>
            <span className="lms-asset-meta">
              {t(`admin.lms.module.type_${a.type}`, a.type)}
              {dur ? ` - ${dur}` : ""}
              {a.sizeMb ? ` - ${a.sizeMb} MB` : ""}
            </span>
          </div>
          <div className="lms-asset-actions">
            {reorderable && assets.length > 1 && (
              <>
                <button
                  className="btn btn-xs"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  aria-label={t("admin.lms.quiz.moveUp")}
                >
                  <i className="fas fa-chevron-up"></i>
                </button>

                <button
                  className="btn btn-xs"
                  disabled={i === assets.length - 1}
                  onClick={() => move(i, 1)}
                  aria-label={t("admin.lms.quiz.moveDown")}
                >
                  <i className="fas fa-chevron-down"></i>
                </button>
              </>
            )}
            <button
              className="btn btn-xs text-danger"
              onClick={() => onDelete?.(a)}
              aria-label={t("common.delete")}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>
      );
    });

  const renderPartAssetTable = (kind) => {
    const isVideo = kind === "video";
    const showSuccess = successDismissedCount !== assets.length;
    const uploadSuccessLabel = isVideo
      ? t("admin.lms.section.videosUploadedSuccess", {
        count: assets.length,
        defaultValue: "{{count}} videos uploaded successfully!",
      })
      : isKeyTips
        ? t("admin.lms.keyTips.uploadedSuccess", {
          count: assets.length,
          defaultValue: "{{count}} Key Tips PDF uploaded successfully!",
        })
        : t("admin.lms.section.summaryUploadedSuccess", {
          count: assets.length,
          defaultValue: "{{count}} PDFs uploaded successfully!",
        });
    const uploadCountLabel = isVideo
      ? t("admin.lms.section.videosUploadedCount", {
        count: assets.length,
        defaultValue: "{{count}} videos uploaded",
      })
      : isKeyTips
        ? t("admin.lms.keyTips.uploadedCount", {
          count: assets.length,
          defaultValue: "{{count}} Key Tips PDF uploaded",
        })
        : t("admin.lms.section.summaryUploadedCount", {
          count: assets.length,
          defaultValue: "{{count}} PDFs uploaded",
        });

    return (
      <div className="lms-part-asset-table-wrap">
        {showSuccess && (
          <div className="lms-part-upload-success">
            <i className="fas fa-check-circle"></i>
            <div>
              <strong>{uploadSuccessLabel}</strong>
              {helperText && <span>{helperText}</span>}
            </div>
            <button
              type="button"
              className="btn lms-part-upload-success-close"
              aria-label={t("common.close", "Close")}
              onClick={() => setSuccessDismissedCount(assets.length)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        <h5 className="lms-part-upload-count">
          {uploadCountLabel}
        </h5>

        <div className="lms-part-asset-table" role="table">
          <div className="lms-part-asset-table-head" role="row">
            <span>#</span>
            <span>{t("admin.lms.asset.thumbnail", "Thumbnail")}</span>
            <span>
              {t(
                isVideo ? "admin.lms.asset.videoTitle" : "admin.lms.asset.fileTitle",
                isVideo ? "Video title" : "File Name"
              )}
            </span>
            {/* <span>{t("admin.lms.asset.duration", "Duration")}</span> */}
            {/* <span>{t("admin.lms.asset.uploadDate", "Upload date")}</span> */}
            <span>{t("admin.lms.asset.status", "Status")}</span>
            <span style={{ textAlign: "center" }}>
              {t("admin.lms.asset.actions", "Actions")}
            </span>
          </div>

          {assets.map((a, i) => {
            const src = a.previewUrl || a.signedUrl || a.url;
            const durationText = formatDuration(a.durationSeconds) || "-";
            const status = a.status || a.publishStatus || "Uploaded";

            return (
              <div
                className={`lms-part-asset-table-row ${dragIndex === i ? "is-dragging" : ""}`}
                role="row"
                key={assetIdOf(a) || i}
                draggable={reorderable && assets.length > 1}
                onDragStart={(e) => {
                  setDragIndex(i);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  dropMove(dragIndex, i);
                }}
                onDragEnd={() => setDragIndex(null)}
              >
                <span className="lms-part-row-order">
                  <i className="fas fa-grip-vertical"></i>
                  {i + 1}
                </span>
                <span className="lms-part-row-thumb">
                  {isVideo && src ? (
                    <>
                      <video src={src} muted preload="metadata">
                        <VideoCaptionTrack asset={a} />
                      </video>
                      {durationText !== "-" && (
                        <em className="lms-part-thumb-duration">{durationText}</em>
                      )}
                    </>
                  ) : a.type === "image" && src ? (
                    <img src={src} alt="" />
                  ) : (
                    <i className={assetIcon(a.type)}></i>
                  )}
                </span>
                <span className="lms-part-row-title">
                  <strong>{a.title || a.filename || "-"}</strong>
                  {a.description && <small>{a.description}</small>}
                </span>
                {/* <span>{durationText}</span> */}
                {/* <span className="lms-part-row-date">{formatUploadDate(a)}</span> */}
                <span>
                  <mark className="lms-part-status-pill">
                    <i className="fas fa-circle"></i>
                    {status}
                  </mark>
                </span>
                <span className="lms-part-row-actions">
                  {src && (
                    <button
                      type="button"
                      className="btn btn-xs lms-local-support-action-btn"
                      onClick={() => setPreviewAsset(a)}
                      aria-label={t("admin.lms.preview.openFull")}
                    >
                      <i className="fas fa-expand"></i>
                    </button>
                  )}
                  <button
                    className="btn btn-xs text-danger"
                    onClick={() => onDelete?.(a)}
                    aria-label={t("common.delete")}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </span>
              </div>
            );
          })}
        </div>

        {/* {reorderable && assets.length > 1 && (
          <div className="lms-part-drag-hint">
            {t(
              isVideo
                ? "admin.lms.section.dragReorderVideos"
                : "admin.lms.section.dragReorderSummary",
              isVideo
                ? "Drag and drop to reorder videos"
                : "Drag and drop to reorder PDFs"
            )}
          </div>
        )} */}
        {previewAsset && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.65)" }}
            role="dialog"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">
                    {previewAsset.title || t("admin.lms.preview.preview")}
                  </h5>
                  <button
                    type="button"
                    className="close btn"
                    onClick={() => setPreviewAsset(null)}
                    style={{ fontSize: "1.5rem" }}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <LmsAssetRenderer asset={previewAsset} showHeader={false} />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn secondary-btn"
                    onClick={() => setPreviewAsset(null)}
                  >
                    {t("common.close", "Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderKeyTipsCards = () => {
    const showSuccess = successDismissedCount !== assets.length;

    return (
      <div className="lms-key-tips-card-wrap">
        {showSuccess && (
          <div className="lms-part-upload-success">
            <i className="fas fa-check-circle"></i>
            <div>
              <strong>
                {t("admin.lms.keyTips.uploadedSuccess", {
                  count: assets.length,
                  defaultValue: "{{count}} Key Tips PDF uploaded successfully!",
                })}
              </strong>
              {helperText && <span>{helperText}</span>}
            </div>
            <button
              type="button"
              className="btn lms-part-upload-success-close"
              aria-label={t("common.close", "Close")}
              onClick={() => setSuccessDismissedCount(assets.length)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {assets.map((asset, i) => {
          const src = asset?.previewUrl || asset?.signedUrl || asset?.url;

          return (
            <div className="lms-key-tips-card" key={assetIdOf(asset) || i}>
              <div className="lms-local-support-head">
                <span className="lms-local-support-number">{i + 1}</span>
                <div className="lms-local-support-title-wrap">
                  <div className="lms-local-support-title">
                    {t("admin.lms.keyTips.cardTitle", "Key Tips PDF")}
                  </div>
                  <div className="lms-local-support-desc">
                    {t(
                      "admin.lms.keyTips.cardDescription",
                      "Learners receive this PDF after completing all Parts."
                    )}
                  </div>
                </div>
                <span className="lms-local-support-badge is-uploaded">
                  {t("admin.lms.asset.uploaded", "Uploaded")}
                  <i className="fas fa-check-circle"></i>
                </span>
              </div>

              <div className="lms-local-support-file">
                <span className="lms-local-support-file-icon">
                  <i className="fas fa-file-pdf"></i>
                </span>
                <div className="lms-local-support-file-main">
                  <strong>{asset.title || asset.filename || "-"}</strong>
                  <span>{t(`admin.lms.module.type_${asset.type}`, asset.type)}</span>
                </div>
                <div className="lms-local-support-file-actions">
                  {src && (
                    <button
                      type="button"
                      className="btn btn-xs lms-local-support-action-btn"
                      onClick={() => setPreviewAsset(asset)}
                      aria-label={t("admin.lms.preview.openFull")}
                    >
                      <i className="fas fa-expand"></i>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-xs text-danger lms-local-support-delete-btn"
                    onClick={() => onDelete?.(asset)}
                    aria-label={t("common.delete")}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {previewAsset && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.65)" }}
            role="dialog"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">
                    {previewAsset.title || t("admin.lms.preview.preview")}
                  </h5>
                  <button
                    type="button"
                    className="close btn"
                    onClick={() => setPreviewAsset(null)}
                    style={{ fontSize: "1.5rem" }}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <LmsAssetRenderer asset={previewAsset} showHeader={false} />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn secondary-btn"
                    onClick={() => setPreviewAsset(null)}
                  >
                    {t("common.close", "Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isPartAsset || isKeyTips) {
    const kind = assetKind || (accept?.includes("video") ? "video" : "pdf");
    const isVideo = kind === "video";

    return (
      <div
        className={`lms-part-asset-list is-${kind} ${isKeyTips ? "is-key-tips" : ""
          }`}
      >
        {assets.length > 0 && (
          <div className="lms-part-asset-actions">
            {/* <button
              type="button"
              className="btn lms-part-reorder-btn btn-with-icon"
              disabled={assets.length < 2}
            >
              <i className="fas fa-sort-amount-down-alt"></i>
              {t("admin.lms.asset.reorder", "Reorder")}
            </button> */}
            {canUpload && (
              <LmsAssetUpload
                accept={accept}
                label={
                  isVideo
                    ? t("admin.lms.asset.addMoreVideo", "Add more video")
                    : isKeyTips
                      ? t("admin.lms.keyTips.addMore", "Add more Key Tips PDF")
                      : t("admin.lms.asset.addMorePdf", "Add more PDF")
                }
                uploadTitle={uploadTitle}
                uploadContext={uploadContext}
                maxMb={isVideo ? 500 : undefined}
                iconClass="fas fa-cloud-upload-alt"
                onUploaded={(asset) => onAdd?.(asset)}
              />
            )}
          </div>
        )}

        {assets.length === 0 && helperText && (
          <div className="lms-part-asset-info">
            <i className="fas fa-info-circle"></i>
            <span>{helperText}</span>
          </div>
        )}

        {assets.length === 0 ? (
          <div className="lms-part-asset-empty">
            <div className="lms-part-empty-art">
              <span className="lms-part-empty-cloud">
                <i className="fas fa-cloud-upload-alt"></i>
              </span>
              <span className="lms-part-empty-frame">
                <i className={isVideo ? "fas fa-image" : "fas fa-file-pdf"}></i>
              </span>
              <span className="lms-part-empty-plant"></span>
            </div>
            <h4>
              {emptyTitle ||
                (isVideo
                  ? t("admin.lms.asset.noVideosUploaded", "No videos uploaded yet")
                  : isKeyTips
                    ? t(
                      "admin.lms.keyTips.emptyTitle",
                      "No Key Tips PDF uploaded yet"
                    )
                    : t(
                      "admin.lms.asset.noSummarySlidesUploaded",
                      "No PDFs uploaded yet"
                    ))}
            </h4>
            <p>
              {emptyBody ||
                (isVideo
                  ? t(
                    "admin.lms.asset.uploadFirstVideo",
                    "Upload your first video to get started."
                  )
                  : isKeyTips
                    ? t(
                      "admin.lms.keyTips.emptyBody",
                      "Upload the Key Tips PDF learners will see after completing this module."
                    )
                    : t(
                      "admin.lms.asset.uploadFirstSummarySlide",
                      "Upload your first PDF to get started."
                    ))}
            </p>
            {canUpload && (
              <LmsAssetUpload
                accept={accept}
                label={addLabel || uploadCopy.upload}
                uploadTitle={uploadTitle}
                uploadContext={uploadContext}
                maxMb={isVideo ? 500 : undefined}
                iconClass="fas fa-cloud-upload-alt"
                onUploaded={(asset) => onAdd?.(asset)}
              />
            )}
            {/* {recommendedText && <small>{recommendedText}</small>} */}
          </div>
        ) : isKeyTips ? (
          renderKeyTipsCards()
        ) : (
          renderPartAssetTable(kind)
        )}
      </div>
    );
  }

  if (isModuleIntro && assets.length === 0) {
    return (
      <div className="lms-module-intro-upload">
        <div className="lms-module-intro-empty-art">
          <span className="lms-module-intro-film">
            <i className="fas fa-play"></i>
          </span>
        </div>
        {/* <h4>{t("admin.lms.moduleIntro.emptyTitle", "No video yet")}</h4> */}
        <p>
          {t(
            "admin.lms.moduleIntro.emptyBody",
            "Upload a video to introduce this module to your learners."
          )}
        </p>
        {canUpload && (
          <LmsAssetUpload
            accept={accept}
            label={addLabel || uploadCopy.upload}
            uploadTitle={uploadTitle}
            uploadContext={uploadContext}
            maxMb={500}
            iconClass="fas fa-cloud-upload-alt"
            onUploaded={(asset) => onAdd?.(asset)}
          />
        )}
      </div>
    );
  }

  if (isModuleIntro) {
    const asset = assets[0];
    const src = asset?.previewUrl || asset?.signedUrl || asset?.url;
    const sizeText = formatAssetSize(asset);
    const durationText = formatDuration(asset?.durationSeconds);

    return (
      <div className="lms-module-intro-video-wrap">
        <div className="lms-intro-video-card lms-module-intro-video-card">
          <div className="lms-intro-video-head">
            <span className="lms-intro-video-number">1</span>
            <div className="lms-intro-video-title-wrap">
              <div className="lms-intro-video-title">
                {t(
                  "admin.lms.moduleIntro.videoTitle",
                  "Module Introduction Video"
                )}
              </div>
              <div className="lms-intro-video-desc">
                {t(
                  "admin.lms.moduleIntro.videoDescription",
                  "Plays before learners begin Part 1 of this module."
                )}
              </div>
            </div>
            <span className="lms-uploaded-badge">
              {t("admin.lms.asset.uploaded", "Uploaded")}
              <i className="fas fa-check-circle"></i>
            </span>
          </div>

          <div className="lms-intro-video-preview">
            {asset.type === "video" && src ? (
              <video
                src={src}
                controls
                preload="metadata"
                controlsList="nodownload"
                onPlay={(e) => {
                  document.querySelectorAll("video").forEach((video) => {
                    if (video !== e.target) video.pause();
                  });
                }}
              >
                <VideoCaptionTrack asset={asset} />
              </video>
            ) : (
              <span className="lms-asset-icon">
                <i className={assetIcon(asset.type)}></i>
              </span>
            )}
          </div>
          <div className="lms-intro-file-row">
            <div className="lms-intro-file-name">
              <i className="fas fa-file-video"></i>
              <span>{asset.title || asset.filename || "-"}</span>
            </div>
            <div className="lms-intro-file-actions">
              {durationText && <span>{durationText}</span>}
              {sizeText && <span>{sizeText}</span>}
              <button
                type="button"
                className="btn btn-xs text-danger lms-intro-delete-btn"
                onClick={() => onDelete?.(asset)}
                aria-label={t("common.delete")}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`lms-asset-list ${isModuleIntro ? "is-module-intro" : ""}`}>
      {assets.length === 0 && (
        <p className="text-center text-muted small mb-2">
          {emptyTitle || uploadCopy.empty}
        </p>
      )}
      {renderAssetCards()}
      {canUpload && (
        <LmsAssetUpload
          accept={accept}
          label={addLabel || uploadCopy.upload}
          uploadTitle={uploadTitle}
          uploadContext={uploadContext}
          onUploaded={(asset) => onAdd?.(asset)}
        />
      )}
    </div>
  );
};

export default LmsAssetList;
